import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

import {
  denormalizeScheduledMessage,
  normalizeConversation,
  normalizeMessage,
  normalizeRecipient,
  normalizeScheduledMessage,
} from './normalizers';

import type {
  AppAlert,
  Conversation,
  Message,
  Recipient,
  ScheduledMessage,
} from './_types';
import type { User } from 'src/models/user/_types';

// Query key functions for consistent caching
export const conversationQueryKeys = {
  // Base keys
  unreadConversations: ['conversations', 'unread'] as const,
  conversations: ['conversations'] as const,
  conversationMessages: (conversationId: Identifier) =>
    ['conversationMessages', conversationId] as const,
  user: (userId: Identifier) => ['user', userId] as const,
  notifyStaffTrainers: () => ['trainers', 'notify-staff'] as const,
  availableRecipients: (searchTerm: string) =>
    ['available recipients', searchTerm] as const,

  // Utility functions for invalidation
  allConversationMessages: () => ['conversationMessages'] as const,
} as const;

interface FetchConversationMessagesOptions {
  conversationId: Conversation['id'];
  page?: number;
  perPage?: number;
}

interface FetchConversationMessagesResponse {
  messages: Message[];
  total: number;
}

export const fetchConversationMessages = async ({
  conversationId,
  page = 1,
  perPage = 5,
}: FetchConversationMessagesOptions): Promise<FetchConversationMessagesResponse> => {
  const response = await api.get(`/api/v4/messages/`, {
    params: {
      page: page,
      per: perPage,
      'q[s]': `created_at desc`,
      'q[conversation_id_eq]': conversationId,
    },
  });
  const messages = response.data.message
    ?.map((v) => normalizeMessage(v))
    .reverse();
  return { messages, total: response.data.meta.total };
};

interface StartNewConversationOptions {
  userIds: string[] | Identifier[];
  receiverId?: Identifier;
  message: string;
  files: any[];
  delivery?: Array<'in-app' | 'email' | 'sms' | 'text'>;
  subject?: string;
  clientBilling?: Identifier | null;
  sendSmsAttachments?: boolean;
}

export const startNewConversation = async ({
  userIds,
  receiverId,
  message,
  files,
  delivery = ['in-app', 'email'],
  subject,
  clientBilling,
  sendSmsAttachments,
}: StartNewConversationOptions): Promise<Conversation> => {
  const response = await api.post(`/api/v4/messages/`, {
    attachment_ids: [],
    message: {
      delivery,
      body: message,
      receiver_id: receiverId
        ? receiverId.toString()
        : userIds.map((u) => u.toString()),
      files: files,
      subject,
      send_sms_attachments: sendSmsAttachments,
      ...(clientBilling ? { client_billing: clientBilling } : {}),
    },
  });
  return normalizeConversation(response.data);
};

interface DeleteConversationOptions {
  conversationId: Conversation['id'];
}

export const deleteConversation = async ({
  conversationId,
}: DeleteConversationOptions): Promise<Conversation> => {
  const response = await api.delete(
    `/api/v4/conversations/${conversationId}/conversation`
  );
  return normalizeConversation(response.data);
};

interface UpdateConversationReadOptions {
  conversationId: Conversation['id'];
  read?: boolean;
}

export const updateConversationRead = async ({
  conversationId,
  read = false,
}: UpdateConversationReadOptions): Promise<Conversation | undefined> => {
  try {
    const response = await api.post(
      `/api/v4/conversations/${conversationId}/toggle_read_by_conversation`,
      {
        read: read,
      }
    );
    return response.data;
  } catch (error: any) {
    // Auto-mark-read (`read: true`) is fired-and-forgotten from background
    // effects/cable handlers where stale or revoked conversation access is
    // expected (see Sentry EXERCISE-COM-2G3K). Swallow the expected 403/404
    // there. User-initiated mark-unread (`read: false`) flows through
    // useMutation and must surface the error so the UI doesn't lie about
    // success.
    if (read && [403, 404].includes(error.response?.status)) {
      return undefined;
    }
    throw error;
  }
};

export const createScheduledMessage = async (
  message: Partial<ScheduledMessage>
) => {
  message.delivery ||= ['email', 'in-app'];
  const response = await api.post(`/api/v3/messages`, {
    message: denormalizeScheduledMessage(message),
  });
  return normalizeScheduledMessage(response.data);
};

interface AddConversationMessageOptions {
  conversationId: Conversation['id'];
  userId: User['id'];
  message: string;
  files: object[];
  sendAt?: Date | null;
  delivery?: Array<'in-app' | 'email' | 'sms' | 'text'>;
  subject?: string;
}

export const addConversationMessage = async ({
  conversationId,
  message,
  files,
  userId,
  delivery = ['in-app', 'email'],
  subject,
  sendAt,
}: AddConversationMessageOptions): Promise<Conversation> => {
  const response = await api.put(
    `/api/v4/conversations/${conversationId}/reply_to_conversation`,
    {
      attachment_ids: [],
      user_id: userId,
      delivery,
      message,
      files,
      subject,
      deliver_at: sendAt,
      override_sender: true,
    }
  );
  return normalizeConversation(response.data);
};

interface UpdateMessageOptions {
  messageId: Message['id'];
  body: string;
}

export const updateMessage = async ({
  messageId,
  body,
}: UpdateMessageOptions): Promise<Message> => {
  const response = await api.patch(`/api/v4/messages/${messageId}`, {
    message: {
      body,
    },
  });
  return normalizeMessage(response.data);
};

export interface ConversationFilters {
  groupId?: string[] | string;
  trainerIds?: string[] | string;
}

export const buildConversationRansackFilters = (
  filters: ConversationFilters
) => {
  const params: Record<string, string[] | string> = {};
  if (filters.groupId) {
    params['q[group_id_eq_any]'] = filters.groupId;
  }
  if (filters.trainerIds) {
    params['q[staff_user_ids_eq_any]'] = Array.isArray(filters.trainerIds)
      ? filters.trainerIds
      : String(filters.trainerIds).split(',').filter(Boolean);
  }
  return params;
};

interface FetchConversationsOptions {
  withUser: boolean;
  sortDir: string;
  search?: string;
  unreadOnly?: boolean;
  page?: number;
  perPage?: number;
  filters?: ConversationFilters;
}

interface FetchConversationsResponse {
  conversations: Conversation[];
  total: number;
}

export const fetchConversations = async ({
  sortDir,
  search,
  page = 1,
  perPage = 20,
  unreadOnly = false,
  filters = {},
}: FetchConversationsOptions): Promise<FetchConversationsResponse> => {
  const response = await api.get(`/api/v4/conversations`, {
    params: {
      page: page,
      per: perPage,
      'q[read_true]': unreadOnly ? false : undefined,
      'q[s]': `updated_at ${sortDir}`,
      ...buildConversationRansackFilters(filters),
      ...(search && {
        'q[conversation_users_formatted_name_or_conversation_users_email_cont]':
          search,
      }),
    },
  });
  const conversations = response.data.conversation?.map((v) =>
    normalizeConversation(v)
  );
  return { conversations, total: response.data.meta.total };
};

interface SearchAvailableRecipientsOptions {
  search: string;
}

export const searchAvailableRecipients = async ({
  search,
}: SearchAvailableRecipientsOptions): Promise<Recipient[]> => {
  const response = await api.get('/api/v4/conversations/available_recipients', {
    params: {
      limit: 10,
      search,
      page: 1,
      autocomplete: true,
    },
  });
  const recipients = response.data.map((foundUser) => {
    return normalizeRecipient({
      ...foundUser,
      nickname: foundUser.term,
    });
  });
  return recipients;
};

interface FetchMessagesOptions {
  withUser: boolean;
  sort: 'asc' | 'desc';
  search: string;
  page?: number;
  perPage?: number;
  unread?: boolean;
  filters?: ConversationFilters;
}

export const fetchMessagesSearch = async ({
  sort,
  search,
  page = 1,
  perPage = 40,
  unread = false,
  filters = {},
}: FetchMessagesOptions): Promise<Array<Message[]>> => {
  const response = await api.get(`/api/v4/messages`, {
    params: {
      page: page,
      per: perPage,
      'q[s]': `created_at ${sort}`,
      search: search,
      unread,
      ...buildConversationRansackFilters(filters),
    },
  });
  const messages = response.data.message?.map((v) => normalizeMessage(v));

  const sortedMessages = messages.reduce((acc, message) => {
    const existingThread = acc[message.conversationId] || [];
    return {
      ...acc,
      [message.conversationId]: [...existingThread, message],
    };
  }, {});

  return Object.keys(sortedMessages).map((key) => sortedMessages[key]);
};

export const fetchScheduledMessages = async ({
  showDelivered = false,
  type,
  page = 1,
  perPage = 20,
}: {
  showDelivered?: boolean;
  type?: 'alert' | 'message';
  page?: number;
  perPage?: number;
} = {}): Promise<any> => {
  const response = await api.get(`/api/v4/scheduled_messages`, {
    params: {
      per: perPage,
      page,
      show_delivered: showDelivered,
      'q[delivery_includes_modal_alert_true]': type === 'alert' ? 1 : 0,
      'q[s]': 'effective_delivery_date desc',
    },
  });
  const messages = response.data.scheduled_message?.map((m) =>
    normalizeScheduledMessage(m)
  );
  return { messages, total: response.data.meta.total };
};

export const deleteScheduledMessage = async (
  message: Partial<ScheduledMessage>
): Promise<ScheduledMessage> => {
  const response = await api.delete(`/api/v4/scheduled_messages/${message.id}`);
  return normalizeScheduledMessage(response.data);
};

interface BulkMessageStatus {
  messageId: Identifier;
  sentCount: number;
  totalCount: number;
}

export const getBulkMessageStatus = async (
  bulkMessageId: string
): Promise<BulkMessageStatus> => {
  const response = await api.get<{
    message_id: Identifier;
    sent_count: number;
    total_count: number;
  }>(`/api/v4/scheduled_messages/${bulkMessageId}/check_status`);

  return camelcaseKeys(response.data);
};

interface FetchAppAlertsResponse {
  alerts: AppAlert[];
  total: number;
}

export const fetchAppAlerts = async (
  alertType: 'modal' | 'banner' = 'modal'
): Promise<FetchAppAlertsResponse> => {
  const response = await api.get('/api/v4/modal_alerts', {
    params: {
      fetch_all: true,
      'q[alert_type_eq]': alertType,
    },
  });

  const alerts = response.data.map((alert) => camelcaseKeys(alert));
  return { alerts, total: alerts.length };
};

/**
 * Fetch banner alerts (app alerts with alert_type = 'banner')
 */
export const fetchBannerAlerts = async (): Promise<FetchAppAlertsResponse> => {
  const response = await api.get('/api/v4/modal_alerts', {
    params: {
      fetch_all: true,
      'q[alert_type_eq]': 'banner',
    },
  });

  const alerts = response.data.map((alert) => camelcaseKeys(alert));
  return { alerts, total: alerts.length };
};
